import type { PassFailFieldStatus } from "@adrekic/fieldsjson"
import { PassFailField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	PassFailArrayExcludesFilterModifier,
	PassFailArrayIncludesFilterModifier,
	PassFailEqualsFilterModifier,
	PassFailNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { PassFailFieldFilterCell } from "./PassFailFieldFilterCell"

type PassFailValue = PassFailFieldStatus | null
type PassFailFilterValue = PassFailFieldStatus | PassFailFieldStatus[] | null

export class PassFailFieldFilterDef extends BaseFilterDef<PassFailValue, PassFailFilterValue> {
	readonly id: string
	readonly defaultFilterValue: PassFailFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: PassFailField

	constructor(field: PassFailField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: PassFailEqualsFilterModifier,
			isValueValid: (value: PassFailFieldStatus | null): value is PassFailFieldStatus => value !== null,
			isFilterValueValid: (
				filterValue: PassFailFieldStatus | PassFailFieldStatus[] | null,
			): filterValue is PassFailFieldStatus => typeof filterValue === "string",
		}),
		notEquals: createFilterModifierConfig({
			modifier: PassFailNotEqualsFilterModifier,
			isValueValid: (value: PassFailFieldStatus | null): value is PassFailFieldStatus => value !== null,
			isFilterValueValid: (
				filterValue: PassFailFieldStatus | PassFailFieldStatus[] | null,
			): filterValue is PassFailFieldStatus => typeof filterValue === "string",
		}),
		includes: createFilterModifierConfig({
			modifier: PassFailArrayIncludesFilterModifier,
			isValueValid: (value: PassFailFieldStatus | null): value is PassFailFieldStatus => value !== null,
			isFilterValueValid: (
				filterValue: PassFailFieldStatus | PassFailFieldStatus[] | null,
			): filterValue is PassFailFieldStatus[] => Array.isArray(filterValue),
		}),
		excludes: createFilterModifierConfig({
			modifier: PassFailArrayExcludesFilterModifier,
			isValueValid: (value: PassFailFieldStatus | null): value is PassFailFieldStatus => value !== null,
			isFilterValueValid: (
				filterValue: PassFailFieldStatus | PassFailFieldStatus[] | null,
			): filterValue is PassFailFieldStatus[] => Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<PassFailFieldFilterDef>): ReactNode {
		return <PassFailFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
